import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { SidePanelComponent } from '../../shared/controls/side-panel/side-panel.component';
import { SwitchControlComponent } from '../../shared/controls/switch-control/switch-control.component';
import { AutoCompleteDropdownComponent } from '../../shared/controls/auto-complete-dropdown/auto-complete-dropdown.component';

import { ErrorService } from '../../shared/services/error-service';
import { ToastService } from '../../shared/toast/toast.service';
import { LoadingService } from '../../shared/services/loading.service';
import { LoadingHelper } from '../../shared/services/loading-helper';

import { UserService, CountryService, UpdateUserInput, GetUserRoleItem, GetUserTenantItem, CreateResult } from '../../../service/proxy';

@Component({
    templateUrl: './create-or-update-user.component.html',
    styleUrls: ['./create-or-update-user.component.scss'],

    imports: [
        CommonModule,
        FormsModule,
        SidePanelComponent,
        SwitchControlComponent,
        AutoCompleteDropdownComponent
    ],

    providers: [
        UserService,
        CountryService
    ]
})
export class CreateOrUpdateUserComponent implements OnInit {
    public userId: number = 0;
    public user: any = { oktaGroup: 'Internal' };

    public formSubmitted: boolean = false;

    public tenants: GetUserTenantItem [] = [];
    public roles: GetUserRoleItem [] = [];
    public reviewers: any[] = [];
    public selectedCountryId: number | undefined;

    public isPanelOpen: boolean = false;
    public isTenantPanel: boolean = false;

    public panelSearch: string = '';
    public panelCaption: string = '';

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private userService: UserService,
        private errorService: ErrorService,
        private toastService: ToastService,
        private loadingService: LoadingService
    ) {}

    ngOnInit(): void {
        this.route.paramMap
            .subscribe(params => {
                this.userId = Number(params.get('id'));

                if (this.userId > 0) {
                    this.getUser(this.userId);
                }

                this.getRoles();
                this.getTenants();
                this.getReviewers();
            }
        );

        this.userId = Number(this.route.snapshot.paramMap.get('id'));
    }

    getUser(userId: number): void {
        LoadingHelper.withLoading(
            this.userService.getUserById(userId),
            this.loadingService
        ).subscribe({
            next: (result) => {
                this.user = result;
                this.user.oktaGroup = result['oktaGroup'] || 'Internal';
                this.selectedCountryId = result['country'];
            },
            error: (error) => {
                this.toastService.show('Failed to retrieve user', 'error');
                this.errorService.handleError(error);
            }
        });
    }

    onCountrySelectionChange(selectedId: number | null): void {
        this.selectedCountryId = selectedId ?? 1;
        this.user.country = this.selectedCountryId;
    }

    onReviewerSelectionChange(selectedId: number | null): void {
        this.user.reviewerId = selectedId;
    }

    displayUserRole(option: any): string {
        return option?.name || '';
    }

    getReviewers(): void {
        LoadingHelper.withLoading(
            this.userService.getUsersForDropdown(this.userId || undefined, undefined),
            this.loadingService
        ).subscribe({
            next: (response) => {
                this.reviewers = response;
            },
            error: (error) => {
                this.toastService.show('Failed to retrieve reviewers', 'error');
                this.errorService.handleError(error);
            }
        });
    }

    getTenants(): void {
        LoadingHelper.withLoading(
            this.userService.getUserTenants(this.userId, this.panelSearch),
            this.loadingService
        ).subscribe({
            next: (response) => {
                this.tenants = response.results;
            },
            error: (error) => {
                this.toastService.show('Failed to retrieve user tenants', 'error');
                this.errorService.handleError(error);
            }
        });
    }

    getRoles(): void {
        LoadingHelper.withLoading(
            this.userService.getUserRoles(this.userId),
            this.loadingService
        ).subscribe({
            next: (response) => {
                this.roles = response.results;
            },
            error: (error) => {
                this.toastService.show('Failed to retrieve user roles', 'error');
                this.errorService.handleError(error);
            }
        });
    }

    onSave(form: any): void {
        if (!form.valid) {
            this.formSubmitted = true;
            return;
        }

        if (this.userId <= 0) {
            this.createUser();
        }
        else {
            this.updateUser();
        }
    }

    get isExternal(): boolean {
        return this.user.oktaGroup === 'External';
    }

    onOktaGroupChange(group: string): void {
        this.user.oktaGroup = group;

        if (group === 'External') {
            this.user.employeeNumber = '0000000';
        } else {
            // Clear the placeholder so the user can enter their real employee number
            if (this.user.employeeNumber === '0000000') {
                this.user.employeeNumber = '';
            }
        }
    }

    onCancel(): void {
        this.router.navigate([`/user`]);
    }

    createUser() {
        LoadingHelper.withLoading(
            this.userService.createUser(this.user),
            this.loadingService
        ).subscribe({
            next: (response: CreateResult) => {
                this.toastService.show('User created successfully', 'success');
                this.router.navigate([`/user`]);
            },
            error: (error) => {
                this.toastService.show('Failed to create user', 'error');
                this.errorService.handleError(error);
            }
        });
    }

    updateUser() {
        const input = this.user as UpdateUserInput;

        LoadingHelper.withLoading(
            this.userService.updateUser(this.userId, input),
            this.loadingService
        ).subscribe({
            next: () => {
                this.toastService.show('User updated successfully', 'success');
                this.router.navigate([`/user`]);
            },
            error: (error) => {
                this.toastService.show('Failed to update user', 'error');
                this.errorService.handleError(error);
            }
        });
    }

    toggleTenantPanel(): void {
        this.isTenantPanel = true;
        this.panelSearch = '';
        this.panelCaption = 'Manage Tenants';
        this.isPanelOpen = !this.isPanelOpen;
    }

    toggleRolePanel(): void {
        this.isTenantPanel = false;
        this.panelSearch = '';
        this.panelCaption = 'Manage Roles';
        this.isPanelOpen = !this.isPanelOpen;
    }

    onPanelDone(): void {
        if (this.isTenantPanel) {
            this.user.tenants = this.tenants.filter(tenant => tenant.isSelected);
        }
        else {
            this.user.roles = this.roles.filter(role => role.isSelected);
        }

        this.isPanelOpen = false;
    }

    onPanelCancel(): void {
        this.isPanelOpen = false;
    }
}
